import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Anchor, Facebook, Instagram, Linkedin, Mail, Menu, Phone, Radio, X } from "lucide-react";
import { SiteProvider, useSite, Wave } from "./site";
import { CookieBanner } from "./Cookie";

function LangSwitch() {
  const { lang, setLang, t } = useSite();
  return (
    <div role="group" aria-label={t.langLabel} className="flex rounded-full ring-1 ring-[#214379]/30 overflow-hidden text-xs font-bold" data-testid="site-lang-switch">
      {[["it", "Italiano"], ["en", "English"]].map(([l, name]) => <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l} aria-label={name} lang={l} className={`px-3 py-1.5 uppercase transition-colors ${lang === l ? "bg-[#214379] text-white" : "text-[#214379] hover:bg-[#214379]/10"}`} data-testid={`site-lang-${l}`}>{l}</button>)}
    </div>
  );
}

function Header() {
  const { t } = useSite();
  const [open, setOpen] = useState(false);
  const link = ({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-[#C8133F]" : "text-[#214379] hover:text-[#007690]"}`;
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm" data-testid="site-header">
      <a href="#contenuto" className="site-skip" data-testid="site-skip-link">{t.skip}</a>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center gap-6">
        <Link to="/" className="shrink-0" data-testid="site-logo"><img src="/site/logo.png" alt="Porto Turistico di Roma" className="h-12 w-auto" /></Link>
        <nav aria-label={t.menuLabel} className="hidden xl:flex items-center gap-6 ml-auto" data-testid="site-nav">{t.menu.map(([to, l]) => <NavLink key={to} to={to} className={link} data-testid={`site-nav-${to.slice(1)}`}>{l}</NavLink>)}</nav>
        <div className="ml-auto xl:ml-4 flex items-center gap-3">
          <LangSwitch />
          <Link to="/login" className="hidden sm:inline-flex rounded-full bg-[#C8133F] hover:bg-[#c9113b] text-white text-xs font-bold px-4 py-2 transition-colors" data-testid="site-crew-link">{t.crew}</Link>
          <button className="xl:hidden text-[#214379] p-2" onClick={() => setOpen(!open)} aria-label={t.menuLabel} aria-expanded={open} aria-controls="site-nav-mobile" data-testid="site-menu-toggle">{open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}</button>
        </div>
      </div>
      {open && (
        <nav id="site-nav-mobile" aria-label={t.menuLabel} className="xl:hidden px-5 pb-6 flex flex-col gap-4" data-testid="site-nav-mobile">
          {t.menu.map(([to, l]) => <NavLink key={to} to={to} className={link} onClick={() => setOpen(false)}>{l}</NavLink>)}
          <Link to="/login" className="text-sm font-bold text-[#C8133F]">{t.crew}</Link>
        </nav>
      )}
    </header>
  );
}

const SOC = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin };

export function SocialLinks({ light }) {
  const { c, t } = useSite();
  const list = (c?.contatti?.social || []).filter((s) => s.url);
  if (!list.length) return null;
  return (
    <ul className="flex gap-3" data-testid={light ? "site-social-footer" : "site-social-contacts"}>{list.map((s) => { const I = SOC[s.nome.toLowerCase()] || Anchor; return <li key={s.nome}><a href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`${s.nome} (${t.newWin})`} className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${light ? "bg-white/10 hover:bg-[#007690] text-white" : "bg-[#214379] hover:bg-[#007690] text-white"}`} data-testid={`site-social-${light ? "f" : "c"}-${s.nome.toLowerCase()}`}><I className="w-5 h-5" aria-hidden="true" /></a></li>; })}</ul>
  );
}

function Footer() {
  const { c, t, setPrefsOpen } = useSite();
  const k = c?.contatti || {};
  return (
    <footer className="bg-[#214379] text-white" data-testid="site-footer">
      <Wave fill="#214379" flip />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-10 grid gap-10 md:grid-cols-3">
        <div className="space-y-4"><img src="/site/logo_white.png" alt="Porto Turistico di Roma" className="h-14 w-auto" data-testid="site-footer-logo" /><p className="text-sm text-white/85 max-w-xs">{c?.porto?.estratto}</p></div>
        <div className="space-y-2 text-sm" data-testid="site-footer-contacts">
          <h2 className="font-bold uppercase tracking-widest text-xs text-white/85 mb-3">{t.contacts}</h2>
          {k.telefono && <a href={`tel:${k.telefono.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#7fd3e3]"><Phone className="w-4 h-4" aria-hidden="true" />{k.telefono}</a>}
          {k.email && <a href={`mailto:${k.email}`} className="flex items-center gap-2 hover:text-[#7fd3e3]"><Mail className="w-4 h-4" aria-hidden="true" />{k.email}</a>}
          {k.torre && <p className="flex items-center gap-2"><Radio className="w-4 h-4" aria-hidden="true" />{k.torre}</p>}
          {k.orari && <p className="text-white/85">{k.orari}</p>}
        </div>
        <div className="space-y-3 text-sm">
      {(k.social || []).some((s) => s.url) && <h2 className="font-bold uppercase tracking-widest text-xs text-white/85">{t.follow}</h2>}
          <SocialLinks light />
          <p className="text-white/85 pt-4">{k.societa}{k.indirizzo && !k.indirizzo.startsWith("[") ? ` · ${k.indirizzo}` : ""}</p>
          <p className="text-white/85">{t.vat} {k.piva} · © {new Date().getFullYear()} {t.rights}</p>
        </div>
      </div>
      <div className="border-t border-white/20"><div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/90" data-testid="site-legal-bar">
        <Link to="/privacy" className="hover:text-white" data-testid="site-privacy-link">{t.privacy}</Link>
        <Link to="/cookie" className="hover:text-white underline-offset-2 hover:underline" data-testid="site-cookie-link">{t.cookie}</Link>
        <Link to="/accessibilita" className="hover:text-white underline-offset-2 hover:underline" data-testid="site-access-link">{t.access}</Link>
        <button onClick={() => setPrefsOpen(true)} className="hover:text-white" data-testid="site-cookie-prefs">{t.prefs}</button>
      </div></div>
      <div className="h-1.5 flex" aria-hidden="true"><span className="flex-1 bg-white" /><span className="flex-1 bg-[#C8133F]" /><span className="flex-1 bg-[#007690]" /></div>
    </footer>
  );
}

const TITLE_PREFIX = "Porto Turistico di Roma - ";

function useHead(pathname) {
  const { t, c } = useSite();
  useEffect(() => {
    const links = [...document.querySelectorAll("link[rel~='icon'], link[rel='apple-touch-icon']")];
    const prev = links.map((l) => l.getAttribute("href"));
    links.forEach((l) => l.setAttribute("href", l.rel === "apple-touch-icon" ? "/site/favicon-192.png" : "/site/favicon-32.png"));
    const title = document.title;
    return () => { links.forEach((l, i) => l.setAttribute("href", prev[i])); document.title = title; };
  }, []);
  useEffect(() => {
    if (pathname.startsWith("/notizie/") || pathname.startsWith("/escursioni/")) { return; }
    const names = { "/": "Home", "/eventi": c?.eventi?.titolo, "/privacy": t.privacy, "/cookie": t.cookie, "/accessibilita": t.access, ...Object.fromEntries(t.menu) };
    document.title = TITLE_PREFIX + (names[pathname] || "Home");
  }, [pathname, t, c]);
}

function Shell({ children }) {
  const { pathname } = useLocation();
  useHead(pathname);
  return (
    <div className="min-h-screen flex flex-col bg-[#F6F9FB] text-slate-800" key={pathname}>
      <Header />
      <main id="contenuto" tabIndex={-1} className="flex-1 outline-none">{children || <Outlet />}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

export default function PublicLayout({ children }) {
  return <SiteProvider><Shell>{children}</Shell></SiteProvider>;
}
